import { useState, useEffect } from 'react';
import { CreditCard, CheckCircle, AlertTriangle, X, RefreshCw, AlertCircle, Eye, EyeOff, Key, Globe, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { getUserSubscription, SubscriptionDetails, getPolarCustomer, createPolarPortalSession, syncSubscriptionStatus, cancelPolarSubscription, downgradeToFree } from '../lib/subscription';
import { SubscriptionModal } from './SubscriptionModal';
import { useAuth } from '../lib/AuthContext';
import { useTranslation } from '../lib/LanguageContext';
import { languageNames } from '../lib/locales';
import { supabase } from '../lib/supabase';
import { meetsPasswordPolicy } from '../lib/passwordPolicy';
import { formatRelativeDays } from '../lib/relativeDays';

interface AccountSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const MCP_SERVER_URL = 'https://www.simplestickers.app/api/mcp';

// .mcp.json for Claude Code (and other clients configured by file)
const MCP_JSON_SNIPPET = `{
  "mcpServers": {
    "simplestickers": {
      "type": "http",
      "url": "${MCP_SERVER_URL}",
      "headers": { "Authorization": "Bearer ss_…" }
    }
  }
}`;

export function AccountSettings({ isOpen, onClose }: AccountSettingsProps) {
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [polarCustomer, setPolarCustomer] = useState<{
    polar_customer_id: string,
    subscription_id: string,
    subscription_status: string,
    cancel_at_period_end: boolean
  } | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [apiKeys, setApiKeys] = useState<{ id: string; name: string; key_prefix: string; source?: 'manual' | 'oauth'; created_at: string; last_used_at: string | null }[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [newApiKeyValue, setNewApiKeyValue] = useState<string | null>(null);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [mcpUrlCopied, setMcpUrlCopied] = useState(false);
  const [showAdvancedApi, setShowAdvancedApi] = useState(false);
  const [activeTab, setActiveTab] = useState<'subscription' | 'plans' | 'api' | 'general'>('subscription');
  const { user } = useAuth();
  const { language, setLanguage, t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      fetchSubscription();
      fetchPolarCustomer();
      fetchApiKeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const data = await getUserSubscription();
      setSubscription(data);
    } catch (err) {
      setError(t('failedToLoadSubscription'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPolarCustomer = async () => {
    if (!user) return;

    try {
      const customer = await getPolarCustomer();
      // Type guard: ensure customer is valid object or null
      if (customer && typeof customer === 'object' && 'polar_customer_id' in customer) {
        setPolarCustomer(customer as {
          polar_customer_id: string;
          subscription_id: string;
          subscription_status: string;
          cancel_at_period_end: boolean;
        });
      } else {
        setPolarCustomer(null);
      }
      console.log('Polar customer data:', customer);
    } catch (err) {
      console.error('Error loading Polar customer:', err);
    }
  };

  const fetchApiKeys = async () => {
    if (!user) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-api`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'list_api_keys' }),
      });
      const data = await res.json();
      if (Array.isArray(data)) setApiKeys(data);
    } catch (err) {
      console.error('Error fetching API keys:', err);
    }
  };

  const handleGenerateApiKey = async () => {
    if (!user) return;
    try {
      setApiKeysLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-api`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'generate_api_key', name: newApiKeyName || 'API Key' }),
      });
      const data = await res.json();
      if (data.key) {
        setNewApiKeyValue(data.key);
        setShowNewKeyModal(true);
        setNewApiKeyName('');
        await fetchApiKeys();
      }
    } catch (err) {
      console.error('Error generating API key:', err);
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleRevokeApiKey = async (keyId: string) => {
    if (!user) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-api`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'revoke_api_key', key_id: keyId }),
      });
      await fetchApiKeys();
    } catch (err) {
      console.error('Error revoking API key:', err);
    }
  };

  const handleCopyMcpUrl = () => {
    navigator.clipboard.writeText(MCP_SERVER_URL);
    setMcpUrlCopied(true);
    setTimeout(() => setMcpUrlCopied(false), 2000);
  };

  const handleCopyKey = () => {
    if (newApiKeyValue) {
      navigator.clipboard.writeText(newApiKeyValue);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  const handleSyncStatus = async () => {
    try {
      setSyncLoading(true);
      setError(null);
      setSyncSuccess(false);

      const success = await syncSubscriptionStatus();

      if (success) {
        setSyncSuccess(true);
        // Refresh data after successful sync
        await fetchSubscription();
        await fetchPolarCustomer();

        // Hide success message after 3 seconds
        setTimeout(() => setSyncSuccess(false), 3000);
      } else {
        throw new Error(t('failedToSyncSubscription'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToSyncSubscription'));
    } finally {
      setSyncLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    if (!polarCustomer?.polar_customer_id) {
      setError(t('noPaymentAccount'));
      return;
    }

    try {
      setPortalLoading(true);
      setError(null);

      // Get the current URL origin dynamically
      const currentOrigin = window.location.origin;
      const returnUrl = currentOrigin;

      console.log('Opening Polar portal for user:', user?.id);
      console.log('Return URL:', returnUrl);

      const url = await createPolarPortalSession(user!.id, returnUrl);
      if (url) {
        // Open in the same window (no target="_blank")
        window.location.href = url;
      } else {
        throw new Error(t('failedToOpenPortal'));
      }
    } catch (err) {
      console.error('Portal error:', err);
      setError(err instanceof Error ? err.message : t('failedToOpenPortal'));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!polarCustomer?.subscription_id) {
      setError(t('noActiveSubscription'));
      return;
    }

    try {
      setCancelLoading(true);
      setError(null);

      const success = await cancelPolarSubscription(polarCustomer.subscription_id);

      if (success) {
        // Refresh data after successful cancellation
        await fetchSubscription();
        await fetchPolarCustomer();
        setShowCancelConfirm(false);
      } else {
        throw new Error(t('failedToCancelSubscription'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToCancelSubscription'));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDowngradeToFree = async () => {
    try {
      setDowngradeLoading(true);
      setError(null);

      const success = await downgradeToFree();

      if (success) {
        // Refresh data after successful downgrade
        await fetchSubscription();
        await fetchPolarCustomer();
        setShowDowngradeConfirm(false);
      } else {
        throw new Error(t('failedToDowngrade'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToDowngrade'));
    } finally {
      setDowngradeLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangePasswordError(null);
    setChangePasswordSuccess(false);

    // Validation
    if (!meetsPasswordPolicy(newPassword)) {
      setChangePasswordError(t('passwordRequirements'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setChangePasswordError(t('newPasswordsDoNotMatch'));
      return;
    }

    if (currentPassword === newPassword) {
      setChangePasswordError(t('passwordMustBeDifferent'));
      return;
    }

    if (!user?.email) {
      setChangePasswordError(t('failedToChangePassword'));
      return;
    }

    try {
      setChangePasswordLoading(true);

      // First verify the current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        throw new Error(t('currentPasswordIncorrect'));
      }

      // If current password is correct, update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      // Success
      setChangePasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowChangePassword(false);
        setChangePasswordSuccess(false);
      }, 2000);
    } catch (err) {
      setChangePasswordError(err instanceof Error ? err.message : t('failedToChangePassword'));
    } finally {
      setChangePasswordLoading(false);
    }
  };

  // Check if user is signed in with email/password (not OAuth)
  const isEmailPasswordUser = () => {
    if (!user) return false;
    // Check if user has email provider in identities
    const hasEmailProvider = user.identities?.some(
      identity => identity.provider === 'email'
    );
    return hasEmailProvider;
  };


  const handleSubscriptionSuccess = () => {
    fetchSubscription();
    fetchPolarCustomer();
  };

  // Helper function to get subscription status display
  const getSubscriptionStatusDisplay = () => {
    if (!polarCustomer) return null;

    const { subscription_status, cancel_at_period_end } = polarCustomer;

    if (subscription_status === 'active' && cancel_at_period_end) {
      return (
        <span className="font-medium text-orange-600">
          {t('statusCanceledNoRenew')}
        </span>
      );
    }

    switch (subscription_status) {
      case 'active':
        return <span className="font-medium text-green-600">{t('statusActive')}</span>;
      case 'canceled':
        return <span className="font-medium text-orange-600">{t('statusCanceled')}</span>;
      case 'past_due':
        return <span className="font-medium text-red-600">{t('statusPastDue')}</span>;
      case 'unpaid':
        return <span className="font-medium text-red-600">{t('statusUnpaid')}</span>;
      default:
        return <span className="font-medium text-gray-600">{subscription_status}</span>;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg h-[600px] max-h-[calc(100vh-32px)] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center border-b px-6 py-4 shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{t('accountSettings')}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab navigation (fixed, outside scroll area).
            pb-px absorbs the 1px the tabs' -mb-px pushes past the content box:
            without it overflow-x-auto promotes overflow-y to auto as well and
            renders a stray vertical scrollbar. */}
        {!loading && !error && subscription && (
          <div className="flex gap-1 border-b px-6 pb-px overflow-x-auto shrink-0">
            {([
              ['subscription', t('subscription')],
              ['plans', t('planComparison')],
              ['api', t('apiKeys')],
              ['general', t('general')],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                  activeTab === key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="px-6 py-4 flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('loadingSubscription')}</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
              {error}
              <button 
                onClick={() => setError(null)} 
                className="ml-2 text-red-700 hover:text-red-900"
              >
                <X className="h-4 w-4 inline" />
              </button>
            </div>
          ) : subscription ? (
            <div>
              {activeTab === 'subscription' && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-base font-medium text-gray-900">{t('subscriptionPlan')}</h4>
                  {polarCustomer && (
                    <div className="flex gap-2">
                      {/* Fix Monthly Sub button hidden but kept for testing purposes
                      <button
                        onClick={handleFixMonthlySubscription}
                        disabled={fixLoading}
                        className="text-xs bg-indigo-100 hover:bg-indigo-200 text-indigo-700 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50"
                        title="Fix monthly subscription expiration date"
                      >
                        <RefreshCw className={`h-3 w-3 ${fixLoading ? 'animate-spin' : ''}`} />
                        {fixLoading ? 'Fixing...' : 'Fix Monthly Sub'}
                      </button>
                      */}
                      <button
                        onClick={handleSyncStatus}
                        disabled={syncLoading}
                        className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50"
                        title="Sync subscription status with Polar"
                      >
                        <RefreshCw className={`h-3 w-3 ${syncLoading ? 'animate-spin' : ''}`} />
                        {syncLoading ? t('syncing') : t('syncStatus')}
                      </button>
                    </div>
                  )}
                </div>

                {syncSuccess && (
                  <div className="bg-green-50 text-green-700 p-3 rounded-md mb-3 text-sm">
                    {t('subscriptionStatusSynced')}
                  </div>
                )}
                
                <div className={`p-4 rounded-lg ${subscription.subscriptionTier === 'premium' ? 'bg-indigo-50' : 'bg-gray-50'} mb-4`}>
                  <div className="flex items-center">
                    {subscription.subscriptionTier === 'premium' ? (
                      <>
                        <CreditCard className="h-5 w-5 text-indigo-600 mr-2" />
                        <span className="font-medium text-indigo-800">{t('premium')}</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 text-gray-600 mr-2" />
                        <span className="font-medium text-gray-800">{t('free')}</span>
                      </>
                    )}
                  </div>

                  <div className="mt-2 text-sm text-gray-600">
                    {subscription.subscriptionTier === 'premium' ? (
                      <>
                        <p>{t('premiumAllows', { limit: subscription.taskLimit })}</p>
                        {subscription.daysRemaining !== null && (
                          <p className="mt-1">
                            {subscription.daysRemaining > 0 ? (
                              <>
                                {polarCustomer?.cancel_at_period_end
                                  ? t('expiresInNoRenew', { when: formatRelativeDays(subscription.daysRemaining, language) })
                                  : t('renewsIn', { when: formatRelativeDays(subscription.daysRemaining, language) })
                                }
                              </>
                            ) : (
                              <>{t('subscriptionExpired')}</>
                            )}
                          </p>
                        )}
                        {polarCustomer && (
                          <p className="mt-1">
                            {t('subscriptionStatus')}: {getSubscriptionStatusDisplay()}
                          </p>
                        )}
                        
                        {/* Show special notice for canceled but active subscriptions */}
                        {polarCustomer?.subscription_status === 'active' && polarCustomer?.cancel_at_period_end && (
                          <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                            <div className="flex items-start">
                              <AlertTriangle className="h-4 w-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                              <div className="text-sm text-orange-800">
                                <p className="font-medium">{t('subscriptionWillNotRenew')}</p>
                                <p className="mt-1">
                                  {t('subscriptionCanceledInfo')}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p>{t('freeAllows', { limit: subscription.taskLimit })}</p>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>{t('stickyNotesUsed')}</span>
                    <span>{subscription.currentTaskCount} of {subscription.taskLimit}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`${subscription.percentageUsed > 90 ? 'bg-red-500' : subscription.percentageUsed > 70 ? 'bg-amber-500' : 'bg-green-500'} h-2 rounded-full`}
                      style={{ width: `${subscription.percentageUsed}%` }}
                    ></div>
                  </div>

                  {subscription.percentageUsed > 90 && (
                    <div className="flex items-center mt-2 text-xs text-red-600">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      <span>{t('almostAtLimit')}</span>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  {subscription.subscriptionTier === 'free' ? (
                    <button
                      onClick={() => setShowSubscriptionModal(true)}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      {t('upgradeToPremium')}
                    </button>
                  ) : (
                    <>
                      {polarCustomer?.polar_customer_id ? (
                        <button
                          onClick={handleOpenPortal}
                          disabled={portalLoading}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          {portalLoading ? t('loading') : t('manageSubscriptionPortal')}
                        </button>
                      ) : (
                        <button
                          onClick={() => setShowSubscriptionModal(true)}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          {t('renewPremium')}
                        </button>
                      )}
                      
                      {/* Testing Cancel Button - Only show for active premium subscriptions */}
                      {polarCustomer?.subscription_status === 'active' && !polarCustomer?.cancel_at_period_end && (
                        <div className="relative" style={{ display: 'none' }}>
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-2">
                            <div className="flex items-start">
                              <AlertCircle className="h-4 w-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                              <div className="text-sm text-amber-800">
                                <p className="font-medium">Testing Feature</p>
                                <p className="text-xs mt-1">
                                  This cancel button is for testing purposes and will be removed in production.
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => setShowCancelConfirm(true)}
                            disabled={cancelLoading}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md shadow-sm text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            <AlertTriangle className="h-4 w-4 mr-2" />
                            {cancelLoading ? 'Canceling...' : 'Cancel Subscription (Test)'}
                          </button>
                        </div>
                      )}
                      
                      {/* Testing Downgrade Button - For immediate free tier conversion */}
                      {subscription.subscriptionTier === 'premium' && (
                        <div className="relative" style={{ display: 'none' }}>
                          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-2">
                            <div className="flex items-start">
                              <AlertCircle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                              <div className="text-sm text-red-800">
                                <p className="font-medium">Testing Feature - Immediate Downgrade</p>
                                <p className="text-xs mt-1">
                                  This will immediately cancel your subscription and switch to free tier. 
                                  This is for testing only and will be removed in production.
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => setShowDowngradeConfirm(true)}
                            disabled={downgradeLoading}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md shadow-sm text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            <AlertTriangle className="h-4 w-4 mr-2" />
                            {downgradeLoading ? 'Downgrading...' : 'Immediate Free Downgrade (Test)'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              )}

              {activeTab === 'general' && (
              <div>
                {/* Language Selector */}
                <div>
                  <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center">
                    <Globe className="h-4 w-4 mr-2" />
                    {t('language')}
                  </h4>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  >
                    {Object.entries(languageNames).map(([code, name]) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Change Password */}
                {isEmailPasswordUser() && (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center">
                      <Key className="h-4 w-4 mr-2" />
                      {t('changePassword')}
                    </h4>
                    <button
                      onClick={() => setShowChangePassword(true)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center"
                    >
                      <Key className="h-4 w-4 mr-2" />
                      {t('changePassword')}
                    </button>
                  </div>
                )}
              </div>
              )}

              {activeTab === 'plans' && (
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-2">{t('planComparison')}</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-medium text-gray-800 mb-2">{t('free')}</h5>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('upToNotes', { count: 50 })}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('taskManagement')}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('templatesManagement')}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('aiSuggestionsPerDay', { count: 10 })}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('receiveShared')}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('limitedSupport')}</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <h5 className="font-medium text-indigo-800 mb-2">{t('premium')}</h5>
                    <div className="text-sm font-medium text-indigo-600 mb-2">
                      {t('monthlyPrice')}
                      <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        {t('yearlyDiscount')}
                      </span>
                    </div>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('upToNotes', { count: 1000 })}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('allFeatures')}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('unlimitedProjects')}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('unlimitedTemplates')}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('aiSuggestionsPerDay', { count: 100 })}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('shareWithTeam')}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('trackSharedStatus')}</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{t('prioritySupport')}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              )}

              {activeTab === 'api' && (
              <div>
                <div className="mb-5 bg-indigo-50 border border-indigo-100 rounded-md p-4">
                  <h4 className="text-base font-medium text-gray-900 mb-1">{t('connectClaudeTitle')}</h4>
                  <p className="text-sm text-gray-600 mb-3">{t('connectClaudeDescription')}</p>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('mcpServerUrl')}</label>
                  <div className="flex items-center gap-2 mb-3">
                    <code className="flex-1 min-w-0 bg-white border border-gray-200 rounded px-3 py-1.5 text-sm font-mono break-all">
                      {MCP_SERVER_URL}
                    </code>
                    <button
                      onClick={handleCopyMcpUrl}
                      className="p-2 text-gray-600 hover:text-indigo-600 flex-shrink-0"
                      title={t('copyToClipboard')}
                    >
                      {mcpUrlCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                    <li>{t('connectClaudeStep1')}</li>
                    <li>{t('connectClaudeStep2')}</li>
                    <li>{t('connectClaudeStep3')}</li>
                  </ol>
                </div>

                <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center">
                  <Key className="h-4 w-4 mr-2" />
                  {t('apiKeys')}
                </h4>

                {apiKeys.length > 0 && (
                  <div className="mb-3 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b">
                          <th className="pb-1 pr-2">{t('apiKeyName')}</th>
                          <th className="pb-1 pr-2">{t('apiKeySource')}</th>
                          <th className="pb-1 pr-2">{t('apiKeyPrefix')}</th>
                          <th className="pb-1 pr-2">{t('apiKeyLastUsed')}</th>
                          <th className="pb-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiKeys.map((k) => (
                          <tr key={k.id} className="border-b last:border-0">
                            <td className="py-1.5 pr-2 font-medium text-gray-800">{k.name}</td>
                            <td className="py-1.5 pr-2 text-gray-500">
                              {k.source === 'oauth' ? t('apiKeySourceOAuth') : t('apiKeySourceManual')}
                            </td>
                            <td className="py-1.5 pr-2 font-mono text-gray-500">{k.key_prefix}…</td>
                            <td className="py-1.5 pr-2 text-gray-500">
                              {k.last_used_at
                                ? new Date(k.last_used_at).toLocaleDateString()
                                : t('apiKeyNeverUsed')}
                            </td>
                            <td className="py-1.5">
                              <button
                                onClick={() => handleRevokeApiKey(k.id)}
                                className="text-xs text-red-600 hover:text-red-800"
                              >
                                {t('revokeApiKey')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    placeholder={t('apiKeyName')}
                    value={newApiKeyName}
                    onChange={(e) => setNewApiKeyName(e.target.value)}
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={handleGenerateApiKey}
                    disabled={apiKeysLoading}
                    className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {apiKeysLoading ? t('loading') : t('generateApiKey')}
                  </button>
                </div>

                <div className="mt-4 border-t pt-3">
                  <button
                    onClick={() => setShowAdvancedApi((v) => !v)}
                    className="flex items-center text-sm font-medium text-gray-700 hover:text-indigo-600"
                  >
                    {showAdvancedApi ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
                    {t('advancedApiUsageTitle')}
                  </button>
                  {showAdvancedApi && (
                    <div className="mt-2 space-y-2">
                      <p className="text-sm text-gray-600">{t('advancedApiUsageDescription')}</p>
                      <pre className="bg-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">{`Authorization: Bearer ss_…`}</pre>
                      <pre className="bg-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">{MCP_JSON_SNIPPET}</pre>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Debug info for troubleshooting */}
              {polarCustomer && (
                <div className="border-t pt-4 mt-4" style={{ display: 'none' }}>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Subscription Details</h4>
                  <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded space-y-1">
                    <p><span className="font-medium">User ID:</span> {user?.id}</p>
                    <p><span className="font-medium">Polar Customer:</span> {polarCustomer?.polar_customer_id || 'None'}</p>
                    <p><span className="font-medium">Subscription:</span> {polarCustomer?.subscription_id || 'None'}</p>
                    <p><span className="font-medium">Status:</span> {polarCustomer?.subscription_status || 'None'}</p>
                    <p><span className="font-medium">Cancel at period end:</span> {polarCustomer?.cancel_at_period_end ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-600">
              {t('noSubscriptionInfo')}
            </div>
          )}
        </div>

        <div className="border-t px-6 py-4 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {t('close')}
          </button>
        </div>
      </div>
      
      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Cancel Subscription</h3>
            </div>
            <div className="px-6 py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="h-4 w-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Testing Feature</p>
                    <p className="text-xs mt-1">
                      This is a temporary testing feature that will be removed in production.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                Are you sure you want to cancel your premium subscription? You'll continue to have access 
                until the end of your current billing period.
              </p>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-2">
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelLoading}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                {cancelLoading ? 'Canceling...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Downgrade to Free Confirmation Modal */}
      {showDowngradeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Immediate Downgrade to Free</h3>
            </div>
            <div className="px-6 py-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium">Testing Feature - Use with Caution</p>
                    <p className="text-xs mt-1">
                      This is for testing purposes only and will be removed in production.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                <strong>Warning:</strong> This will immediately cancel your Polar subscription and 
                switch your account to the free tier. You will lose premium access right away, 
                not at the end of the billing period.
              </p>
              <p className="text-gray-600 text-sm mb-4">
                This action is designed for testing the downgrade functionality and should not be 
                used with real subscriptions in production.
              </p>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-2">
              <button
                onClick={() => setShowDowngradeConfirm(false)}
                disabled={downgradeLoading}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDowngradeToFree}
                disabled={downgradeLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                {downgradeLoading ? 'Processing...' : 'Downgrade to Free Now'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showSubscriptionModal && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          onSuccess={handleSubscriptionSuccess}
        />
      )}

      {/* New API Key Modal */}
      {showNewKeyModal && newApiKeyValue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{t('apiKeyCreated')}</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="flex items-start">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800">{t('apiKeyCopyWarning')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 rounded px-3 py-2 text-sm font-mono break-all">
                  {newApiKeyValue}
                </code>
                <button
                  onClick={handleCopyKey}
                  className="p-2 text-gray-600 hover:text-indigo-600 flex-shrink-0"
                  title="Copy"
                >
                  {keyCopied ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button
                onClick={() => {
                  setShowNewKeyModal(false);
                  setNewApiKeyValue(null);
                  setKeyCopied(false);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{t('changePassword')}</h3>
            </div>
            <form onSubmit={handleChangePassword}>
              <div className="px-6 py-4 space-y-4">
                {/* Current Password */}
                <div>
                  <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('currentPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="current-password"
                      type={showCurrentPassword ? "text" : "password"}
                      required
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('newPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('confirmNewPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {changePasswordError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{changePasswordError}</p>
                  </div>
                )}

                {/* Success Message */}
                {changePasswordSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3">
                    <p className="text-sm text-green-800">{t('passwordChangedSuccess')}</p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setChangePasswordError(null);
                    setChangePasswordSuccess(false);
                  }}
                  disabled={changePasswordLoading}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={changePasswordLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {changePasswordLoading ? t('changing') : t('changePassword')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}